import React from 'react';
import { colors } from '../../styles/tokens';
import { fmtPct, ring } from '../../utils/format';
import { GLOSARIO, semaforo } from '../../utils/glosario';
import { useEsMovil } from '../../hooks/useEsMovil';

export interface MedidoresProps {
  /** Fracción 0..1 (o `null` si no hay consultas): Solicitudes / Consultas. */
  efect: number | null;
  /** Fracción 0..1 (o `null` si no hay solicitudes): Atendidas / Solicitudes. */
  sla: number | null;
  /**
   * Fracción 0..1 (o `null` si no hay atendidas): Venta / Atendidas (V ÷ A).
   * Idealmente <= 1 (toda la venta pasa por el sistema) — puede superar 1
   * con datos reales cuando hay venta de piso fuera del sistema (ver la nota
   * junto al título de esta tarjeta y `tasaColor`, que colorea ALTO como
   * MALO / no-cumplimiento).
   */
  tasa: number | null;
}

/** Circunferencia del anillo SVG (`r=34`, igual que el design handoff). */
const RING_CIRC = 2 * Math.PI * 34;

interface GaugeProps {
  label: string;
  sub: string;
  color: string;
  /** Fracción 0..1 (o `null`). */
  value: number | null;
  note?: string;
  /** Disposición de teléfono (ver `useEsMovil`); se recibe por prop para no montar 3 listeners de `matchMedia`. */
  esMovil?: boolean;
}

const Gauge: React.FC<GaugeProps> = ({ label, sub, color, value, note, esMovil = false }) => {
  const pct0a100 = value === null ? null : value * 100;
  const dash = ring(pct0a100, RING_CIRC);

  return (
    <div
      style={{
        background: colors.card,
        border: `1px solid ${colors.border}`,
        borderRadius: 16,
        // En 390 px, 20 px de padding lateral por lado se los come el anillo:
        // el texto de al lado se quedaba sin sitio y partía palabras.
        padding: esMovil ? '14px 14px' : '16px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: esMovil ? 14 : 18,
        boxShadow: '0 1px 2px rgba(20,28,54,.05)',
      }}
    >
      <div style={{ position: 'relative', width: 82, height: 82, flex: 'none' }}>
        <svg width="82" height="82" viewBox="0 0 84 84">
          <circle cx="42" cy="42" r="34" fill="none" stroke={colors.ringTrack} strokeWidth={8} />
          <circle
            cx="42"
            cy="42"
            r="34"
            fill="none"
            stroke={color}
            strokeWidth={8}
            strokeLinecap="round"
            strokeDasharray={dash}
            transform="rotate(-90 42 42)"
          />
        </svg>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 16,
            fontWeight: 800,
            color,
          }}
        >
          {fmtPct(value, 0)}
        </div>
      </div>
      {/*
        `minWidth: 0` SÓLO en móvil: sin él, un hijo de flex no baja de su
        ancho de contenido y la glosa larga ("Solicitudes a Bodega vs
        Consultas Totales") ensancha la tarjeta en vez de repartir el texto en
        varias líneas. En escritorio se deja el `min-width: auto` de siempre,
        que es lo que decide hoy dónde parten esas frases: tocarlo cambiaría
        el corte de línea en la columna más estrecha del `auto-fit`.
      */}
      <div style={esMovil ? { minWidth: 0, flex: 1 } : undefined}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: '.04em',
            textTransform: 'uppercase',
            color: colors.texto.quaternary,
          }}
        >
          {label}
          {/*
            El ícono "i" sólo se pinta en escritorio: su explicación vive en un
            `title`, y un tooltip nativo necesita puntero — en un teléfono no
            hay hover, así que en móvil la nota sería INALCANZABLE. Ahí se
            imprime como texto, más abajo.
          */}
          {note && !esMovil && (
            <span
              title={note}
              aria-label={note}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 13,
                height: 13,
                borderRadius: '50%',
                background: colors.ringTrack,
                color: colors.texto.tertiary,
                fontSize: 9.5,
                fontWeight: 800,
                textTransform: 'none',
                letterSpacing: 0,
                cursor: 'help',
              }}
            >
              i
            </span>
          )}
        </div>
        <div
          style={{
            fontSize: 26,
            fontWeight: 800,
            color: colors.texto.primary,
            fontVariantNumeric: 'tabular-nums',
            marginTop: 2,
          }}
        >
          {fmtPct(value)}
        </div>
        <div style={{ fontSize: 11.5, color: colors.texto.tertiary, marginTop: 1 }}>{sub}</div>
        {note && esMovil && (
          <div
            style={{
              fontSize: 11,
              lineHeight: 1.35,
              color: colors.texto.tertiary,
              marginTop: 6,
              paddingTop: 6,
              borderTop: `1px solid ${colors.ringTrack}`,
            }}
          >
            {note}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * 3 medidores (anillos SVG) del embudo Global: Efectividad de inventario
 * (azul marca), Atendidas por bodeguero y Tasa de atención (con su semáforo,
 * `utils/glosario`). Con el modo «Activas / Sin uso» acotan la OPERACIÓN de
 * esas tiendas, igual que el embudo (ver `aggregateGlobal`). Recreado
 * pixel-exact de `Tablero Colectivo Moda.dc.html` (líneas 79-83): anillo
 * `r=34` / `stroke-width=8` rotado -90°, % centrado (16px/800) + valor grande
 * (26px/800) y sub (11.5px) a la derecha.
 *
 * `tasa` es la única que puede superar 100% con datos reales (venta de piso
 * fuera del sistema) — se marca con un pequeño ícono "i" con tooltip nativo
 * (`title`) aclarando que ideal es <=100% y que un valor mayor es una señal
 * de no-cumplimiento (la tienda no registra toda su venta en la
 * herramienta). Su color YA no es fijo (`colors.dorado`): sale de
 * `tasaColor`, que invierte el semáforo habitual — ALTO es MALO aquí.
 *
 * EN TELÉFONO: una tarjeta por fila a todo el ancho. El `auto-fit` ya daba
 * una sola columna a 390 px, pero su mínimo de 220 px es un ancho FIJO: en un
 * contenedor más estrecho la columna se queda en 220 px y desborda. Con
 * `minmax(0, 1fr)` la tarjeta nunca puede ser más ancha que su sitio.
 */
const Medidores: React.FC<MedidoresProps> = ({ efect, sla, tasa }) => {
  const esMovil = useEsMovil();

  return (
    <div
      data-testid="medidores-globales"
      style={{
        display: 'grid',
        gridTemplateColumns: esMovil ? 'minmax(0, 1fr)' : 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: esMovil ? 12 : 14,
        marginTop: esMovil ? 12 : 14,
      }}
    >
      {/* Rótulos, fórmulas y semáforos del glosario: los mismos que las
          tarjetas de la vista Tienda. «Atendidas por bodeguero» era verde
          fijo aquí y tenía semáforo en la tabla: ahora el mismo en los dos. */}
      <Gauge
        label={GLOSARIO.efectividad.rotulo}
        sub={`${GLOSARIO.efectividad.nota} · ${GLOSARIO.efectividad.formula}`}
        color={semaforo('efectividad', efect)}
        value={efect}
        esMovil={esMovil}
      />
      <Gauge
        label={GLOSARIO.atendidasBodega.rotulo}
        sub={`${GLOSARIO.atendidasBodega.nota} · ${GLOSARIO.atendidasBodega.formula}`}
        color={semaforo('atendidasBodega', sla)}
        value={sla}
        esMovil={esMovil}
      />
      <Gauge
        label={GLOSARIO.tasa.rotulo}
        sub={`Pares vendidos ÷ solicitudes atendidas · ${GLOSARIO.tasa.formula} · ideal ≤ 100%`}
        color={semaforo('tasa', tasa)}
        value={tasa}
        note={GLOSARIO.tasa.nota}
        esMovil={esMovil}
      />
    </div>
  );
};

export default Medidores;
